import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Search, MoreHorizontal, CheckCircle2, Truck, PackageCheck, Hand, Ban, Undo2, RotateCcw, FileText, Loader2, ReceiptText, MapPin, Mail, Phone, MessageSquare, BellRing, Package, X } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ShopNav } from "../components/shop/ShopNav";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errorMessage, formatDateTime, formatDate } from "../lib/api";
import { addressLine, deliveryLabel, fulfillmentMeta, isInvoiceOverdue, nok, orderStatusMeta, paymentLabel } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "open", label: "Til behandling" },
  { key: "invoice_open", label: "Åpne fakturaer" },
  { key: "pending_payment", label: "Venter på Vipps" },
  { key: "done", label: "Ferdige" },
  { key: "cancelled", label: "Kansellerte" },
  { key: "refunded", label: "Refunderte" },
];

export default function ShopOrdersPage() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const productFilter = params.get("product") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind: cancel|refund, order}
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/my/shop/orders", { params: { status, ...(productFilter ? { product: productFilter } : {}) } });
      setOrders(res.data.orders);
    } catch (err) {
      toast.error(errorMessage(err));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [status, productFilter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!orders) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => [o.order_number, o.name, o.email, o.invoice_number || "", ...(o.items || []).flatMap((i) => [i.name, i.sku || ""])].join(" ").toLowerCase().includes(needle));
  }, [orders, q]);

  // Namnet på vara det er filtrert på (frå første ordre som har henne)
  const productFilterName = useMemo(() => {
    if (!productFilter || !orders) return "";
    for (const o of orders) {
      const hit = (o.items || []).find((i) => i.product_id === productFilter);
      if (hit) return hit.sku ? `${hit.name} (${hit.sku})` : hit.name;
    }
    return "";
  }, [orders, productFilter]);

  const clearProductFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("product");
    setParams(next);
  };

  const setStatus = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set("status", key); else next.delete("status");
    setParams(next);
  };

  const replace = (order) => {
    setOrders((list) => list.map((o) => (o.id === order.id ? order : o)));
    setDetail((d) => (d && d.id === order.id ? order : d));
  };

  const act = async (order, path, body) => {
    setBusy(order.id);
    try {
      const res = await api.post(`/my/shop/orders/${order.id}/${path}`, body || {});
      replace(res.data.order);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const runConfirm = async () => {
    const { kind, order } = confirm;
    setConfirm(null);
    await act(order, kind, { reason });
    setReason("");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/my/shop/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "butikk-ordrer.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // Teljaren i undermenyen er berre påliteleg når lista ikkje er filtrert på status (elles skjul han)
  const openCount = orders && (status === "" || status === "open") ? orders.filter((o) => ["paid", "invoice_sent"].includes(o.status) && ["pending", "ready"].includes(o.fulfillment)).length : 0;
  // Påminning: maks éin per døgn per ordre (same regel som backend)
  const remindedRecently = (o) => !!o.reminder_sent_at && Date.now() - new Date(o.reminder_sent_at).getTime() < 24 * 60 * 60 * 1000;

  // sfx: "" (tabell) | "-m" (mobilkort) | "-d" (dialog) – unike data-testid sjølv om begge visningane ligg i DOM (CSS skjuler éi)
  const Actions = ({ o, sfx = "" }) => {
    const canFulfil = ["paid", "invoice_sent"].includes(o.status);
    const pickup = o.delivery_method === "pickup";
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t("Handlinger")} disabled={busy === o.id} data-testid={`order-actions-${o.id}${sfx}`}>
            {busy === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setDetail(o)} data-testid={`order-action-detail-${o.id}`}><FileText className="w-4 h-4 mr-2" /> {t("Detaljer")}</DropdownMenuItem>
          {o.invoice_url && <DropdownMenuItem asChild><a href={`${process.env.REACT_APP_BACKEND_URL}${o.invoice_url}`} target="_blank" rel="noreferrer" data-testid={`order-action-invoice-${o.id}`}><ReceiptText className="w-4 h-4 mr-2" /> {t("Last ned faktura (PDF)")}</a></DropdownMenuItem>}
          <DropdownMenuSeparator />
          {o.status === "invoice_sent" && <DropdownMenuItem onClick={() => act(o, "mark-paid")} data-testid={`order-action-mark-paid-${o.id}`}><CheckCircle2 className="w-4 h-4 mr-2" /> {t("Marker betalt")}</DropdownMenuItem>}
          {o.status === "invoice_sent" && (
            <DropdownMenuItem onClick={() => act(o, "remind")} disabled={remindedRecently(o)} data-testid={`order-action-remind-${o.id}`}>
              <BellRing className="w-4 h-4 mr-2" /> {remindedRecently(o) ? t("Påminnelse sendt i dag") : o.reminder_count ? t("Send påminnelse igjen") : t("Send påminnelse")}
            </DropdownMenuItem>
          )}
          {o.status === "paid" && o.payment_method === "invoice" && <DropdownMenuItem onClick={() => act(o, "mark-unpaid")} data-testid={`order-action-mark-unpaid-${o.id}`}><Undo2 className="w-4 h-4 mr-2" /> {t("Sett tilbake til ubetalt")}</DropdownMenuItem>}
          {canFulfil && pickup && o.fulfillment === "pending" && <DropdownMenuItem onClick={() => act(o, "fulfill", { state: "ready" })} data-testid={`order-action-ready-${o.id}`}><PackageCheck className="w-4 h-4 mr-2" /> {t("Klar for henting")}</DropdownMenuItem>}
          {canFulfil && pickup && o.fulfillment !== "picked_up" && <DropdownMenuItem onClick={() => act(o, "fulfill", { state: "picked_up" })} data-testid={`order-action-picked-up-${o.id}`}><Hand className="w-4 h-4 mr-2" /> {t("Marker hentet")}</DropdownMenuItem>}
          {canFulfil && !pickup && o.fulfillment !== "shipped" && <DropdownMenuItem onClick={() => act(o, "fulfill", { state: "shipped" })} data-testid={`order-action-shipped-${o.id}`}><Truck className="w-4 h-4 mr-2" /> {t("Marker sendt")}</DropdownMenuItem>}
          {["pending_payment", "processing", "invoice_sent"].includes(o.status) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => setConfirm({ kind: "cancel", order: o })} data-testid={`order-action-cancel-${o.id}`}><Ban className="w-4 h-4 mr-2" /> {t("Kanseller")}</DropdownMenuItem>
            </>
          )}
          {o.status === "paid" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => setConfirm({ kind: "refund", order: o })} data-testid={`order-action-refund-${o.id}`}><RotateCcw className="w-4 h-4 mr-2" /> {t("Refunder")}</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const StatusBadges = ({ o, sfx = "" }) => {
    const st = orderStatusMeta(o);
    const fu = fulfillmentMeta(o);
    const overdue = isInvoiceOverdue(o);
    const showFulfil = ["paid", "invoice_sent"].includes(o.status);
    return (
      <div className="flex flex-wrap gap-1.5">
        <Badge className={overdue ? "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]" : st.className} data-testid={`order-status-${o.id}${sfx}`}>{overdue ? t("Forfalt") : st.label}</Badge>
        {showFulfil && <Badge className={fu.className} data-testid={`order-fulfillment-${o.id}${sfx}`}>{fu.label}</Badge>}
        {o.status === "invoice_sent" && o.reminder_sent_at && (
          <Badge variant="outline" className="gap-1 font-normal text-muted-foreground" data-testid={`order-reminded-${o.id}${sfx}`}><BellRing className="w-3 h-3" /> {t("Påminnet {date}", { date: formatDate(o.reminder_sent_at) })}</Badge>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="shop-orders-page">
        <ShopNav openCount={openCount} />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Ordrer i Butikk")} {orders && <span className="text-sm font-normal text-muted-foreground">({filtered.length})</span>}</h2>
          <div className="flex gap-2">
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk på ordrenr, navn, e-post, vare eller varenr.")} className="pl-9" data-testid="orders-search-input" />
            </div>
            <Button variant="outline" onClick={exportCsv} data-testid="orders-export-button"><Download className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">{t("Eksporter CSV")}</span><span className="sm:hidden">CSV</span></Button>
          </div>
        </div>
        {productFilter && (
          <div className="flex items-center gap-2 text-sm" data-testid="orders-product-filter">
            <Badge variant="outline" className="gap-1.5 font-normal py-1 pl-2.5 pr-1">
              <Package className="w-3.5 h-3.5 text-muted-foreground" />
              {productFilterName ? t("Ordrer med {name}", { name: productFilterName }) : t("Ordrer med valgt vare")}
              <button type="button" onClick={clearProductFilter} className="ml-1 rounded-full p-0.5 hover:bg-accent" aria-label={t("Fjern varefilter")} data-testid="orders-product-filter-clear"><X className="w-3.5 h-3.5" /></button>
            </Badge>
          </div>
        )}
        <div className="flex flex-wrap gap-2" data-testid="orders-status-filter">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors duration-150 ${status === f.key ? "bg-[hsl(var(--accent))] text-foreground border-[hsl(var(--brand))]/30" : "bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground"}`}
              aria-pressed={status === f.key}
              data-testid={`orders-filter-${f.key || "all"}`}
            >
              {t(f.label)}
            </button>
          ))}
        </div>

        {loading || !orders ? (
          <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-border" data-testid="empty-orders-state">
            <CardContent className="p-10 text-center space-y-2">
              <p className="font-heading font-semibold">{orders.length === 0 && !q && !productFilter ? t("Ingen ordre") : t("Ingen ordre passet filteret")}</p>
              <p className="text-sm text-muted-foreground">{t("Når noen kjøper i Butikk, dukker ordrene opp her.")}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-border hidden md:block">
              <Table data-testid="orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Ordre")}</TableHead>
                    <TableHead>{t("Kjøper")}</TableHead>
                    <TableHead>{t("Varer")}</TableHead>
                    <TableHead>{t("Levering")}</TableHead>
                    <TableHead>{t("Betaling")}</TableHead>
                    <TableHead className="text-right">{t("Beløp")}</TableHead>
                    <TableHead>{t("Status")}</TableHead>
                    <TableHead className="text-right">{t("Handlinger")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                      <TableCell>
                        <button type="button" onClick={() => setDetail(o)} className="text-left" data-testid={`order-open-${o.id}`}>
                          <span className="block font-mono text-sm font-medium">{o.order_number}</span>
                          <span className="block text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(o.created_at)}</span>
                        </button>
                      </TableCell>
                      <TableCell><span className="block font-medium">{o.name}</span><span className="block text-xs text-muted-foreground">{o.email}</span></TableCell>
                      <TableCell className="text-sm max-w-[220px]"><span className="line-clamp-2">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</span></TableCell>
                      <TableCell className="text-sm">{deliveryLabel(o)}</TableCell>
                      <TableCell className="text-sm">
                        {paymentLabel(o)}
                        {o.invoice_number && <span className="block text-xs text-muted-foreground font-mono">{o.invoice_number} · {t("Forfall {date}", { date: formatDate(o.invoice_due_at) })}</span>}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{nok(o.total_amount)}</TableCell>
                      <TableCell><StatusBadges o={o} /></TableCell>
                      <TableCell className="text-right"><Actions o={o} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="space-y-3 md:hidden" data-testid="orders-card-list">
              {filtered.map((o) => (
                <Card key={o.id} className="border-border" data-testid={`order-mobile-card-${o.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <button type="button" onClick={() => setDetail(o)} className="text-left min-w-0">
                        <span className="block font-mono text-sm font-medium">{o.order_number}</span>
                        <span className="block text-sm truncate">{o.name}</span>
                        <span className="block text-xs text-muted-foreground">{formatDateTime(o.created_at)}</span>
                      </button>
                      <Actions o={o} sfx="-m" />
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</p>
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadges o={o} sfx="-m" />
                      <span className="font-semibold tabular-nums">{nok(o.total_amount)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{deliveryLabel(o)} · {paymentLabel(o)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Detaljar */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{t("Ordre {number}", { number: detail.order_number })}</DialogTitle>
                <DialogDescription>{formatDateTime(detail.created_at)} · {paymentLabel(detail)} · {deliveryLabel(detail)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <StatusBadges o={detail} sfx="-d" />
                <div className="rounded-lg border border-border divide-y divide-border">
                  {detail.items.map((i, idx) => (
                    <div key={`${i.product_id}-${idx}`} className="flex justify-between gap-3 p-3">
                      <span>{i.name} × {i.quantity}{i.sku && <span className="block font-mono text-xs text-muted-foreground" data-testid={`order-detail-item-sku-${idx}`}>{t("Varenr.")} {i.sku}</span>}</span>
                      <span className="tabular-nums">{nok(i.line_total)}</span>
                    </div>
                  ))}
                  {detail.shipping_price > 0 && <div className="flex justify-between gap-3 p-3 text-muted-foreground"><span>{t("Frakt")}</span><span className="tabular-nums">{nok(detail.shipping_price)}</span></div>}
                  <div className="flex justify-between gap-3 p-3 font-semibold"><span>{t("Totalt")}</span><span className="tabular-nums">{nok(detail.total_amount)}</span></div>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <p className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-muted-foreground" /><span><span className="block font-medium">{detail.name}</span><a href={`mailto:${detail.email}`} className="underline underline-offset-2">{detail.email}</a></span></p>
                  {detail.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /><a href={`tel:${detail.phone}`} className="underline underline-offset-2">{detail.phone}</a></p>}
                  {detail.address && <p className="flex items-start gap-2 sm:col-span-2"><MapPin className="w-4 h-4 mt-0.5 text-muted-foreground" /><span>{addressLine(detail.address)}</span></p>}
                  {detail.note && <p className="flex items-start gap-2 sm:col-span-2 rounded-lg bg-muted/50 p-3"><MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground" /><span><span className="block text-xs text-muted-foreground">{t("Din melding")}</span>{detail.note}</span></p>}
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5" data-testid="order-detail-history">
                  {detail.invoice_number && <p>{t("Faktura {number}", { number: detail.invoice_number })} · {t("Forfall {date}", { date: formatDate(detail.invoice_due_at) })}</p>}
                  {detail.paid_at && <p>{t("Betalt {date}", { date: formatDateTime(detail.paid_at) })}{detail.marked_paid_by ? ` · ${t("Markert betalt av {by}", { by: detail.marked_paid_by })}` : ""}</p>}
                  {(detail.reminder_history || []).map((h, i) => <p key={`r${i}`} data-testid={`order-detail-reminder-${i}`}>{t("Påminnelse sendt")} · {formatDateTime(h.at)} · {h.by === "system" ? t("Automatisk") : h.by}</p>)}
                  {(detail.fulfillment_history || []).map((h, i) => <p key={i}>{fulfillmentMeta({ fulfillment: h.state }).label} · {formatDateTime(h.at)}</p>)}
                  {detail.cancelled_at && <p>{t("Kansellert")} · {formatDateTime(detail.cancelled_at)}{detail.cancel_reason ? ` · ${detail.cancel_reason}` : ""}</p>}
                  {detail.refunded_at && <p>{t("Refundert")} · {formatDateTime(detail.refunded_at)} · {nok(detail.refund_amount)}{detail.refund_reason ? ` · ${detail.refund_reason}` : ""}</p>}
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                {detail.invoice_url && <Button asChild variant="outline" data-testid="order-detail-invoice-button"><a href={`${process.env.REACT_APP_BACKEND_URL}${detail.invoice_url}`} target="_blank" rel="noreferrer"><ReceiptText className="w-4 h-4 mr-1.5" /> {t("Last ned faktura (PDF)")}</a></Button>}
                <Button variant="secondary" onClick={() => setDetail(null)} data-testid="order-detail-close">{t("Lukk")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Kanseller / refunder */}
      <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) { setConfirm(null); setReason(""); } }}>
        <DialogContent data-testid="order-confirm-dialog">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{confirm.kind === "cancel" ? t("Kansellere bestillingen?") : t("Refundere hele bestillingen?")}</DialogTitle>
                <DialogDescription>
                  {confirm.kind === "cancel"
                    ? t("Varene legges tilbake på lager og kjøperen får beskjed på e-post. Du kan skrive en kort forklaring til kjøperen.")
                    : confirm.order.payment_method === "vipps"
                      ? t("Vipps-betalingen ({amount}) refunderes fra din Vipps-konto til kjøperen, og varene legges tilbake på lager.", { amount: nok(confirm.order.total_amount) })
                      : t("Du betaler tilbake {amount} til kjøperen selv (bankoverføring). Bestillingen markeres som refundert og varene legges tilbake på lager.", { amount: nok(confirm.order.total_amount) })}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="order-reason">{t("Begrunnelse til kjøperen (valgfritt)")}</Label>
                <Textarea id="order-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} data-testid="order-reason-textarea" />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="secondary" onClick={() => { setConfirm(null); setReason(""); }} data-testid="order-confirm-cancel">{t("Avbryt")}</Button>
                <Button onClick={runConfirm} className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:opacity-95" data-testid="order-confirm-submit">{confirm.kind === "cancel" ? t("Ja, kanseller") : t("Ja, refunder")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
